import * as React from 'react';
import { css, useTheme } from '@emotion/react';
import { Draft, Link, Stack, Text } from '@resi-media/resi-ui';
import { type FallbackRender } from '@sentry/react';
import { useAtomValue, useSetAtom } from 'jotai';
import { match } from 'ts-pattern';
import { alertError } from '@studio/helpers/alertError';
import { usePrefix } from '@studio/hooks';
import AppError from '@svg/AppError.svg';
import UnsupportedBrowserError from '@svg/UnsupportedBrowserError.svg';
import { browserStateAtom } from '../../storeV2/browser';
import { RerouteInfo } from '../RerouteInfo';

type ResetError = Parameters<FallbackRender>[0]['resetError'];

type _Props = {
  error: unknown;
  resetError: ResetError;
};

// Errors thrown when a code-split chunk can't be fetched — almost always a stale chunk
// reference after a redeploy (the old hashed filename no longer exists on the server).
const dynamicImportErrors = [
  'error loading dynamically imported module',
  'Importing a module script failed',
  'Failed to fetch dynamically imported module',
];

// A full reload pulls a fresh index.html + chunk manifest, which resolves stale-chunk failures.
// The timestamp guard prevents a reload loop if the chunk is genuinely missing (reloading wouldn't fix it).
const RELOAD_GUARD_KEY = 'chunk-load-reload-at';
const RELOAD_GUARD_WINDOW_MS = 10_000;

const reloadForStaleChunk = () => {
  try {
    const lastReloadAt = Number(window.sessionStorage.getItem(RELOAD_GUARD_KEY)) || 0;
    if (Date.now() - lastReloadAt <= RELOAD_GUARD_WINDOW_MS) return false;
    window.sessionStorage.setItem(RELOAD_GUARD_KEY, String(Date.now()));
    window.location.reload();
    return true;
  } catch {
    // sessionStorage unavailable (private mode, quota exceeded, cross-origin) — skip auto-recovery
    return false;
  }
};

const ErrorBoundaryFallback = ({ error, resetError }: _Props) => {
  const { commonT, linkT, prefixNS, PrefixTrans } = usePrefix('components:', 'errorBoundary');
  const theme = useTheme();

  const browserAtom = useAtomValue(browserStateAtom);
  const setBrowserState = useSetAtom(browserStateAtom);
  const mounted = React.useRef(false);

  React.useEffect(() => {
    if (mounted.current || !browserAtom.isBrowserSupported) {
      return;
    }
    mounted.current = true;
    const message = error instanceof Error ? error.message : String(error);
    if (dynamicImportErrors.some((dynamicImportError) => message.includes(dynamicImportError))) {
      // Recover automatically instead of stranding the user on the crash page — the "Retry"
      // button can't help here, since it just re-runs the same failing import.
      reloadForStaleChunk();
      return;
    }
    alertError(`error boundary - ${message}`);
  }, [browserAtom.isBrowserSupported, error]);

  React.useEffect(() => {
    setBrowserState((prevState) => ({
      ...prevState,
      isUnsupportedBannerVisible: false,
    }));
  }, [setBrowserState]);

  return (
    <div data-testid="error-boundary-page">
      {match(browserAtom.isBrowserSupported)
        .with(true, () => (
          <RerouteInfo
            contentHeading={commonT('unexpectedErrorOccurred')}
            ctaButton={<Draft.Button label={commonT('retry')} onClick={resetError} variant="outlined" />}
            header={
              <Stack
                alignItems="center"
                css={css`
                  min-height: ${theme.typography.pxToRem(205)};
                  background-color: ${theme.palette.primary.active};
                `}
                justifyContent="center"
              >
                <Text
                  colorVariant="inverted"
                  css={css`
                    font-size: ${theme.typography.pxToRem(80)};
                  `}
                  variant="h1"
                >
                  {commonT('oops')}
                </Text>
              </Stack>
            }
            paragraph1={prefixNS('refreshToTryAgain')}
            paragraph2={
              <PrefixTrans i18nKey="contactIfPersists">
                fill
                <Link href={linkT('contact')} rel="noopener noreferrer" target="_blank" variant="body2">
                  contactSupport
                </Link>
              </PrefixTrans>
            }
            svg={<AppError />}
          />
        ))
        .with(false, () => (
          <RerouteInfo
            contentHeading={prefixNS('unsupportedBrowser.description1')}
            header={
              <Stack
                alignItems="center"
                css={css`
                  min-height: ${theme.typography.pxToRem(205)};
                  background-color: ${theme.palette.primary.active};
                `}
                justifyContent="center"
              >
                <Text colorVariant="inverted" textAlign="center" variant="h2">
                  {prefixNS('unsupportedBrowser.title')}
                </Text>
              </Stack>
            }
            paragraph1={prefixNS('unsupportedBrowser.description2')}
            paragraph2={
              <PrefixTrans i18nKey="unsupportedBrowser.description3">
                fill
                <Link href={linkT('contact')} rel="noopener noreferrer" target="_blank" variant="body2">
                  updateBrowser
                </Link>
              </PrefixTrans>
            }
            svg={<UnsupportedBrowserError />}
          />
        ))
        .exhaustive()}
    </div>
  );
};

ErrorBoundaryFallback.displayName = 'ErrorBoundaryFallback';
export default ErrorBoundaryFallback;
